import axios, {AxiosError, AxiosRequestConfig} from 'axios'
import {getAuth as getFirebaseAuth} from 'firebase/auth'
import {AuthModel} from './_models'
import {emitAuthExpired, isAuthSessionError} from './session'

const AUTH_LOCAL_STORAGE_KEY = 'kt-auth-react-v'

const getAuth = (): AuthModel | undefined => {
  if (!localStorage) {
    return
  }

  const lsValue: string | null = localStorage.getItem(AUTH_LOCAL_STORAGE_KEY)
  if (!lsValue) {
    return
  }

  try {
    const auth: AuthModel = JSON.parse(lsValue) as AuthModel
    if (auth) {
      return auth
    }
  } catch (error) {
    console.error('AUTH LOCAL STORAGE PARSE ERROR', error)
  }
}

const setAuth = (auth: AuthModel) => {
  if (!localStorage) {
    return
  }

  try {
    const lsValue = JSON.stringify(auth)
    localStorage.setItem(AUTH_LOCAL_STORAGE_KEY, lsValue)
  } catch (error) {
    console.error('AUTH LOCAL STORAGE SAVE ERROR', error)
  }
}

const removeAuth = () => {
  if (!localStorage) {
    return
  }

  try {
    localStorage.removeItem(AUTH_LOCAL_STORAGE_KEY)
  } catch (error) {
    console.error('AUTH LOCAL STORAGE REMOVE ERROR', error)
  }
}

export function setupAxios(axiosInstance: typeof axios) {
  axiosInstance.defaults.headers.common.Accept = 'application/json'

  axiosInstance.interceptors.request.use(
    async (config: AxiosRequestConfig) => {
      const headers = config.headers || {}
      const firebaseUser = getFirebaseAuth().currentUser

      if (firebaseUser) {
        const token = await firebaseUser.getIdToken()
        headers.Authorization = `Bearer ${token}`
      } else {
        const auth = getAuth()
        if (auth?.api_token) {
          headers.Authorization = `Bearer ${auth.api_token}`
        }
      }

      config.headers = headers
      return config
    },
    (err) => Promise.reject(err)
  )

  axiosInstance.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      const status = error.response?.status
      if (status === 401 || isAuthSessionError(error)) {
        emitAuthExpired()
      }
      return Promise.reject(error)
    }
  )
}

export {getAuth, setAuth, removeAuth, AUTH_LOCAL_STORAGE_KEY}
